import { useCallback, useEffect, useMemo, useState } from "react";

import Fuse from "fuse.js";

import { useFeature } from "../features";
import { useObjectMemo } from "../../../hooks";
import { useDebounce } from "../../../hooks/useDebouncedEffect.hook";
import { useAccounts } from "../../entities";

import { useCreateSearch } from "./requests";
import {
  DEFAULT_SEARCH_ENTITIES,
  INITIAL_REMOTE_CACHE,
  MIN_SEARCH_TERM_LENGTH,
  SEARCH_DELAY_MS,
} from "./constants";

import type { GlobalSearchEntities, RemoteSearchCache } from "./types";
import type Account from "../../../../Account";

const isSearchTermValid = (term: string) =>
  term.length >= MIN_SEARCH_TERM_LENGTH;

export const useIsGlobalSearchEnabled = () => {
  const { featureEnabled, features } = useFeature();
  const enabledSearchEntities =
    features?.olb_global_search_entities || DEFAULT_SEARCH_ENTITIES;
  const isSearchEnabled = useMemo(
    () =>
      featureEnabled("olb_global_search") &&
      Object.values(enabledSearchEntities).some((enabled) => enabled),
    [enabledSearchEntities, featureEnabled],
  );

  return useObjectMemo({ isSearchEnabled, enabledSearchEntities });
};

export const useGlobalSearch = () => {
  const { enabledSearchEntities } = useIsGlobalSearchEnabled();
  const { createSearch, loading } = useCreateSearch();
  const allAccounts = useAccounts();
  const accounts = useMemo(
    () => allAccounts.filter((acc) => acc.isInternal()),
    [allAccounts],
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [accountSearchResults, setAccountSearchResults] = useState<Account[]>(
    [],
  );
  const [remoteCacheResults, setRemoteCacheResults] =
    useState<RemoteSearchCache>(INITIAL_REMOTE_CACHE);
  const [resultsFilter, setResultsFilter] =
    useState<GlobalSearchEntities>("all");

  const fuseAccounts = useMemo(
    () =>
      new Fuse(accounts, {
        keys: ["product_type", "name", "nickname"],
        includeScore: true,
        threshold: 0.3,
        ignoreLocation: true,
      }),
    [accounts],
  );

  const localSearchHandler = useCallback(
    (input: string) => {
      const accountsResult = fuseAccounts.search(input);
      setAccountSearchResults(accountsResult.map((r) => r.item));
    },
    [fuseAccounts],
  );

  const remoteSearchHandler = useCallback(
    (input: string) => {
      if (!enabledSearchEntities.pages && !enabledSearchEntities.transactions) {
        return;
      }
      createSearch(input, {
        onSuccess({ results: { pages = [], transactions = [] } }) {
          setRemoteCacheResults((currentCache) => {
            const updated = new Map(currentCache);
            updated.set(input, {
              pages,
              transactions: transactions.slice(0, 5),
            });
            return updated;
          });
        },
      });
    },
    [enabledSearchEntities, createSearch],
  );

  const debouncedRemoteSearch = useDebounce(
    remoteSearchHandler,
    SEARCH_DELAY_MS,
    {
      trailing: true,
      leading: false,
    },
  );

  /**
   * Performs a search if at least 3 characters have been typed.
   * If input is cleared, reset the search state.
   * If a previous search has already been sent, update the results with
   * the cached results.
   */
  const searchTermChangeHandler = useCallback(
    (input: string) => {
      setSearchTerm(input);

      if (!isSearchTermValid(input)) {
        debouncedRemoteSearch.cancel();
        return;
      }

      localSearchHandler(input);
      if (!remoteCacheResults.get(input)) {
        debouncedRemoteSearch(input);
      }
    },
    [localSearchHandler, remoteCacheResults, debouncedRemoteSearch],
  );

  // Cancel any pending search on unmount
  useEffect(
    () => () => debouncedRemoteSearch.cancel(),
    [debouncedRemoteSearch],
  );

  const remoteSearchDisabled =
    !enabledSearchEntities.pages && !enabledSearchEntities.transactions;
  const cachedRemoteResults = remoteCacheResults.get(searchTerm);
  const pagesResults = cachedRemoteResults?.pages || [];
  const transactionsResults = cachedRemoteResults?.transactions || [];
  const displayedAccounts = useMemo(
    // prettier-ignore
    () => (isSearchTermValid(searchTerm) ? accountSearchResults : accounts).slice(0, 5),
    [searchTerm, accountSearchResults, accounts],
  );
  const remoteSearchComplete =
    remoteSearchDisabled || cachedRemoteResults !== undefined;

  return useObjectMemo({
    searchTerm,
    searchTermChangeHandler,
    resultsFilter,
    setResultsFilter,
    loading,
    pagesResults,
    transactionsResults,
    accountSearchResults: displayedAccounts,
    noResultsFound:
      isSearchTermValid(searchTerm) &&
      remoteSearchComplete &&
      [accountSearchResults, pagesResults, transactionsResults].every(
        (group) => group.length === 0,
      ),
  });
};
